import { useTranslation } from '@/lib/i18n';
import { KpiCard, KpiGrid } from '@/components/ui';
import { useMileageBalanceQuery } from '@/features/mileage/hooks/useAdminMileageQuery';
import { useDepositBalanceQuery } from '@/features/mileage/hooks/useAdminDepositQuery';
import type { AdminMemberDetail } from '../hooks/useAdminUsersQuery';
import { userMallLabel } from '../lib/userLabels';

interface UserSummaryCardProps {
  user: AdminMemberDetail;
}

const formatDate = (iso: string | null, fallback: string): string =>
  iso ? new Date(iso).toLocaleDateString('ko-KR') : fallback;

/**
 * 회원 요약 카드 (BUG-020 Part 2 + 마일리지/예치금 잔액).
 *
 * 이름 / 가입일 / 마지막 로그인 + 보유 마일리지 / 예치금 잔액을 노출한다.
 * 잔액은 admin mileage/deposit balance 엔드포인트로 조회한다.
 */
export const UserSummaryCard = ({ user }: UserSummaryCardProps) => {
  const { t } = useTranslation('common');
  const mileageBalance = useMileageBalanceQuery(user.userId);
  const depositBalance = useDepositBalanceQuery(user.userId);

  return (
    <KpiGrid columns={3} className="mb-4">
      {/* 소속 국가몰을 첫 칸에 둔다 — 상세에서 수행하는 상태변경·마일리지 조정이 어느 몰의
          계정에 적용되는지 운영자가 먼저 확인해야 한다(몰마다 별개 계정). */}
      <KpiCard
        density="compact"
        label={t('users.summary.mallCode', '소속 국가몰')}
        value={userMallLabel(t, user.mallCode)}
      />
      <KpiCard
        density="compact"
        label={t('users.summary.name')}
        value={user.name}
      />
      <KpiCard
        density="compact"
        label={t('users.summary.createdAt')}
        value={formatDate(user.createdAt, t('users.summary.notProvided'))}
      />
      <KpiCard
        density="compact"
        label={t('users.summary.lastLoginAt')}
        value={formatDate(user.lastLoginAt, t('users.summary.never'))}
      />
      <KpiCard
        density="compact"
        label="보유 마일리지"
        value={mileageBalance.isPending ? '...' : `${(mileageBalance.data?.balance ?? 0).toLocaleString()}P`}
      />
      <KpiCard
        density="compact"
        label="예치금 잔액"
        value={depositBalance.isPending ? '...' : `${(depositBalance.data?.balance ?? 0).toLocaleString()}원`}
      />
    </KpiGrid>
  );
};
